// Generator: https://app.quicktype.io/?l=ts
import * as z from "zod";
import { artSlotBindings, artSlotStyleNames } from "./art-fields";
import { metadataDefinitions, metadataReferences } from "./metadata-schema";
import { defaultElementPosition } from "../schedule-art/layout-coordinates";

export const BindSchema = z.enum(artSlotBindings);
export type Bind = z.infer<typeof BindSchema>;

export const FormatSchema = z.string().meta({ reference: "formats", initial: "clock" });
export type Format = z.infer<typeof FormatSchema>;

export const StyleSchema = z.string().meta({ reference: "styles", initial: "stream_title" });
export type Style = z.infer<typeof StyleSchema>;

export const TypeSchema = z.enum(["icon"]);
export type Type = z.infer<typeof TypeSchema>;

const CoordinatesSchema = z.array(z.number()).length(2).meta({ initial: [0, 0], coordinates: true });
const ElementCoordinatesSchema = CoordinatesSchema.meta({ initial: defaultElementPosition() });

const RotationSchema = z.number().meta({ omittedValue: 0 }).optional();

export const DateClassSchema = z.object({
  style: StyleSchema.meta({ initial: "week_date" }),
  format: FormatSchema.meta({ initial: "week_date" }),
  offset: ElementCoordinatesSchema,
  rotation: RotationSchema,
});
export type DateClass = z.infer<typeof DateClassSchema>;

export const OfflineSchema = z.object({
  bind: z.string().meta({ initial: "offline.message" }),
  style: StyleSchema.meta({ initial: "offline_label" }),
  offset: ElementCoordinatesSchema,
  rotation: RotationSchema,
});
export type Offline = z.infer<typeof OfflineSchema>;

export const SlotSchema = z.object({
  bind: BindSchema,
  type: TypeSchema.optional(),
  black_icon: z.boolean().optional(),
  offset: ElementCoordinatesSchema,
  rotation: RotationSchema,
  scale: z.number().meta({ initial: 100, omittedValue: 100 }).optional(),
  style: StyleSchema.optional(),
  format: FormatSchema.meta({ visibleForBindings: ["slot1.time", "slot2.time"] }).optional(),
}).meta({ initial: { bind: "slot1.title", style: "stream_title", offset: defaultElementPosition() } });
export type Slot = z.infer<typeof SlotSchema>;

export const FormatsSchema = z
  .object({})
  .catchall(z.string().meta({ initial: "%d" }))
  .meta({ definition: "formats" });
export type Formats = z.infer<typeof FormatsSchema>;

export const GlobalBindSchema = z.enum([
  "week.start_date",
  "week.end_date",
  "week.range",
  "profile.artist_handle",
]);

const dateBindings = ["week.start_date", "week.end_date", "week.range"];

export const GlobalSchema = z.object({
  bind: GlobalBindSchema,
  style: StyleSchema.meta({ initial: "week_range" }),
  format: FormatSchema.meta({ initial: "week_date", visibleForBindings: dateBindings }).optional(),
  position: ElementCoordinatesSchema,
  rotation: RotationSchema,
});
export type Global = z.infer<typeof GlobalSchema>;

export const TextStrokeSchema = z.object({
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).meta({ initial: "#000000" }),
  // Native canvas pixels: full centered width, or outward thickness for outside.
  width: z.number().nonnegative().meta({ initial: 1 }),
  position: z.enum(["center", "outside"]).optional(),
});

export const OfflineLabelSchema = z.object({
  font: z.string().meta({ initial: "sans-serif" }),
  size: z.number().meta({ initial: 28 }),
  color: z.string().meta({ initial: "#000000" }),
  weight: z.number().meta({ initial: 400 }),
  stroke: TextStrokeSchema.optional(),
  capitalize: z.boolean().optional(),
  align: z.enum(["left", "center", "right"]).optional(),
});
export type OfflineLabel = z.infer<typeof OfflineLabelSchema>;

export const FriSchema = z.object({
  origin: CoordinatesSchema,
  rotation: RotationSchema,
  offline: z.array(OfflineSchema),
  one_slot: z.array(SlotSchema),
  two_slot: z.array(SlotSchema),
  date: DateClassSchema,
});
export type Fri = z.infer<typeof FriSchema>;

type OptionalStylesShape = { [Name in (typeof artSlotStyleNames)[number]]: z.ZodOptional<typeof OfflineLabelSchema> };
const optionalStylesShape = Object.fromEntries(
  artSlotStyleNames.map(name => [name, OfflineLabelSchema.optional()]),
) as OptionalStylesShape;

export const StylesSchema = z
  .object({
    ...optionalStylesShape,
    art_creator: OfflineLabelSchema,
    week_range: OfflineLabelSchema,
    stream_title: OfflineLabelSchema,
    stream_subtitle: OfflineLabelSchema,
    stream_time: OfflineLabelSchema,
    stream_title_compact: OfflineLabelSchema,
    stream_subtitle_compact: OfflineLabelSchema,
    stream_time_compact: OfflineLabelSchema,
    offline_label: OfflineLabelSchema,
    week_date: OfflineLabelSchema,
  })
  .catchall(OfflineLabelSchema)
  .meta({ definition: "styles" });
export type Styles = z.infer<typeof StylesSchema>;

export const DaysSchema = z.object({
  mon: FriSchema,
  tue: FriSchema,
  wed: FriSchema,
  thu: FriSchema,
  fri: FriSchema,
  sat: FriSchema,
  sun: FriSchema,
});
export type Days = z.infer<typeof DaysSchema>;

export const ArtMetadataSchema = z
  .object({
    styles: StylesSchema,
    formats: FormatsSchema,
    globals: z.array(GlobalSchema),
    days: DaysSchema,
  })
  .superRefine((metadata, context) => {
    const references = metadataReferences(ArtMetadataSchema, metadata);
    references.forEach(reference => {
      const definitions = metadataDefinitions(metadata, reference.definition);
      if (Object.hasOwn(definitions, reference.name) && definitions[reference.name] !== undefined) return;
      context.addIssue({ code: "custom", path: reference.path, message: "missingDefinition" });
    });
    metadata.globals.forEach((element, index) => {
      if (element.format === undefined) return;
      if (element.bind.startsWith("profile.")) {
        context.addIssue({
          code: "custom",
          path: ["globals", index, "format"],
          message: "textFormat",
        });
      }
    });
  });
export type ArtMetadata = z.infer<typeof ArtMetadataSchema>;
export type ArtDate = DateClass;
export type ArtElement = Global | Offline | Slot;
export type ArtTextStyle = OfflineLabel;
